import { SceneComponent } from '@/_models/virtualStaging/Scene.component';
import { LoadingState } from '@/_models/virtualStaging/VSObject';
import { Outputs } from '@/_models/virtualStaging/VRPointer/component/types';
import { ControllerType } from '@/_models/virtualStaging/HTMLMesh/component/Menu/types';

interface GamepadEvent {
  controller: ControllerType;
  button: number;
  type: string;
}

export class VREventManager extends SceneComponent {
  public root: any = null;

  public pivot: any = null;

  public hostPose: any;

  public eventListeners = [];

  controllers: any;

  public controllerL: any;

  public controllerR: any;

  public gamepadL: any;

  public gamepadR: any;

  public activeButtons = {
    gamepadL: new Map(),
    gamepadR: new Map(),
  };

  public activeAxes = {
    gamepadL: new Map(),
    gamepadR: new Map(),
  };

  inputs: any = {
    visible: false,
  };

  outputs: Outputs = {
    loadingState: LoadingState.Loaded,
  };

  declare context: any;

  // Centralized button handlers - can be overridden by BaseTour
  public buttonHandlers = {
    // A Button (Right Controller Button 4) - AI Avatar toggle
    aButton: () => this.emitVREvent('vrAButton'),
    // B Button (Right Controller Button 5) - Flow restart
    bButton: () => this.emitVREvent('vrBButton'),
    // X Button (Left Controller Button 4) - Flow next step
    xButton: () => this.emitVREvent('vrXButton'),
    // Y Button (Left Controller Button 5) - Flow position
    yButton: () => this.emitVREvent('vrYButton'),
    // Thumbstick movement
    thumbstick: (axis: number) => this.emitVREvent('vrThumbstick', { axis }),
  };

  onInit() {
    this.getXRController();
    this.initCentralizedButtonHandlers();
  }

  /**
   * Initialize centralized VR button handlers
   * This sets up all VR button events in one place for easier management
   */
  initCentralizedButtonHandlers() {
    // A Button (Right Controller Button 4) - AI Avatar toggle
    this.addEventListener(
      { type: 'pointerdown', button: 4, controller: ControllerType.GamepadR },
      () => this.buttonHandlers.aButton(),
    );

    // B Button (Right Controller Button 5) - Flow restart
    this.addEventListener(
      { type: 'pointerdown', button: 5, controller: ControllerType.GamepadR },
      () => this.buttonHandlers.bButton(),
    );

    // X Button (Left Controller Button 4) - Flow next step
    this.addEventListener(
      { type: 'pointerdown', button: 4, controller: ControllerType.GamepadL },
      () => this.buttonHandlers.xButton(),
    );

    // Y Button (Left Controller Button 5) - Flow position
    this.addEventListener(
      { type: 'pointerdown', button: 5, controller: ControllerType.GamepadL },
      () => this.buttonHandlers.yButton(),
    );

    // Thumbstick movement (both controllers)
    this.addEventListener(
      { type: 'thumbstickthreshholddown', button: 3, controller: ControllerType.GamepadL },
      (axis) => this.buttonHandlers.thumbstick(axis),
    );

    this.addEventListener(
      { type: 'thumbstickthreshholddown', button: 3, controller: ControllerType.GamepadR },
      (axis) => this.buttonHandlers.thumbstick(axis),
    );
  }

  /**
   * Emit VR events to the document for other components to listen to
   * This allows decoupled communication between VR events and business logic
   */
  emitVREvent(eventName: string, detail: any = {}) {
    const event = new CustomEvent(eventName, { detail });
    document.dispatchEvent(event);
  }

  /**
   * Override button handlers from external components (like BaseTour)
   * This allows customization of VR button behavior
   */
  setButtonHandlers(handlers: Partial<typeof this.buttonHandlers>) {
    this.buttonHandlers = { ...this.buttonHandlers, ...handlers };
  }

  getXRController() {
    const { xr } = this.context.renderer;

    this.controllers = [xr.getController(0), xr.getController(1)];

    this.controllers.forEach((controller) => {
      controller.addEventListener('connected', (event) => {
        const inputSource = event.data;
        const { handedness } = inputSource;

        if (handedness === 'left') {
          this.controllerL = controller;
          this.gamepadL = inputSource.gamepad;
        } else if (handedness === 'right') {
          this.controllerR = controller;
          this.gamepadR = inputSource.gamepad;
        }
      });
    });
  }

  onInputsUpdated(previousInputs: this['inputs']) {
    this.root.visible = this.inputs.visible;
  }

  addEventListener(event: GamepadEvent, callback: any) {
    this.eventListeners.push({ event, callback });
  }

  removeEventListener(event: GamepadEvent, callback: any) {
    const listenerToRemove = this.eventListeners.find((listener) => listener.callback === callback);
    if (listenerToRemove)
      this.eventListeners.splice(this.eventListeners.indexOf(listenerToRemove), 1);
  }

  cycleVR(_session) {
    if (this.gamepadL) {
      this.gamepadL.buttons.forEach((button, index) => {
        if (button.pressed && !this.activeButtons.gamepadL.get(index)) {
          const listeners = this.eventListeners.filter(
            (listener) =>
              listener.event.controller === ControllerType.GamepadL &&
              listener.event.button === index &&
              listener.event.type === 'pointerdown',
          );
          listeners.forEach((listener) => listener.callback(button));
        }
        if (!button.pressed && this.activeButtons.gamepadL.get(index)) {
          const listeners = this.eventListeners.filter(
            (listener) =>
              listener.event.controller === ControllerType.GamepadL &&
              listener.event.button === index &&
              listener.event.type === 'pointerup',
          );
          listeners.forEach((listener) => listener.callback(button));
        }
        this.activeButtons.gamepadL.set(index, button.pressed);
      });

      this.gamepadL.axes.forEach((axis, index) => {
        if (!this.activeAxes.gamepadL.get(index) && (axis < -0.5 || axis > 0.5)) {
          this.activeAxes.gamepadL.set(index, true);
          const listeners = this.eventListeners.filter(
            (listener) =>
              listener.event.controller === ControllerType.GamepadL &&
              listener.event.button === index &&
              listener.event.type === 'thumbstickthreshholddown',
          );
          listeners.forEach((listener) => listener.callback(axis));
        }
        if (this.activeAxes.gamepadL.get(index) && axis >= -0.5 && axis <= 0.5) {
          this.activeAxes.gamepadL.set(index, false);
          const listeners = this.eventListeners.filter(
            (listener) =>
              listener.event.controller === ControllerType.GamepadL &&
              listener.event.button === index &&
              listener.event.type === 'thumbstickthreshholdup',
          );
          listeners.forEach((listener) => listener.callback(axis));
        }
      });
    }
    if (this.gamepadR) {
      this.gamepadR.buttons.forEach((button, index) => {
        if (button.pressed && !this.activeButtons.gamepadR.get(index)) {
          const listeners = this.eventListeners.filter(
            (listener) =>
              listener.event.controller === ControllerType.GamepadR &&
              listener.event.button === index &&
              listener.event.type === 'pointerdown',
          );
          listeners.forEach((listener) => listener.callback(button));
        }
        if (!button.pressed && this.activeButtons.gamepadR.get(index)) {
          const listeners = this.eventListeners.filter(
            (listener) =>
              listener.event.controller === ControllerType.GamepadR &&
              listener.event.button === index &&
              listener.event.type === 'pointerup',
          );
          listeners.forEach((listener) => listener.callback(button));
        }
        this.activeButtons.gamepadR.set(index, button.pressed);
      });

      this.gamepadR.axes.forEach((axis, index) => {
        if (!this.activeAxes.gamepadR.get(index) && (axis < -0.5 || axis > 0.5)) {
          this.activeAxes.gamepadR.set(index, true);
          const listeners = this.eventListeners.filter(
            (listener) =>
              listener.event.controller === ControllerType.GamepadR &&
              listener.event.button === index &&
              listener.event.type === 'thumbstickthreshholddown',
          );
          listeners.forEach((listener) => listener.callback(axis));
        }
        if (this.activeAxes.gamepadR.get(index) && axis >= -0.5 && axis <= 0.5) {
          this.activeAxes.gamepadR.set(index, false);
          const listeners = this.eventListeners.filter(
            (listener) =>
              listener.event.controller === ControllerType.GamepadR &&
              listener.event.button === index &&
              listener.event.type === 'thumbstickthreshholdup',
          );
          listeners.forEach((listener) => listener.callback(axis));
        }
      });
    }
  }

  onTick() {
    if (this.context?.renderer?.xr?.isPresenting) {
      const _session = this.context.renderer.xr.getSession();
      _session.requestAnimationFrame(() => this.cycleVR(_session));
    }
  }
}

export const VREventManagerComponentType = 'mp.VREventManagerComponent';
export const makeVREventManagerComponent = (): any => new VREventManager();
